const CONVERGENCE_DURATION = 1600;
const PART_STAGGER = 60;
const TRANSITION_TIMEOUT = CONVERGENCE_DURATION + PART_STAGGER * 2 + 200;

// Presentation only: an unavailable animation must never block a valid login.
export function createLoginTransition({ background, brand, headline, card }) {
  let resolveFinished;
  let settled = false;
  let timeout;
  const animations = [];
  const finished = new Promise((resolve) => { resolveFinished = resolve; });

  const settle = (completed) => {
    if (settled) return;
    settled = true;
    if (timeout !== undefined) window.clearTimeout(timeout);
    resolveFinished(completed);
  };

  const cancelAnimations = () => {
    animations.forEach((animation) => {
      // Cancelling rejects finished, including on a partially created transition.
      animation.finished?.catch(() => {});
      try {
        animation.cancel();
      } catch {
        // Some older implementations cannot cancel an already-finished animation.
      }
    });
  };

  const controller = {
    finished,
    cancel: () => {
      settle(false);
      cancelAnimations();
    },
  };

  const parts = [background, brand, headline];
  const reducedMotion = typeof window.matchMedia === "function"
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (reducedMotion || [card, ...parts].some((element) => !element || typeof element.animate !== "function")) {
    settle(true);
    return controller;
  }

  try {
    const cardBounds = card.getBoundingClientRect();
    if (!cardBounds.width || !cardBounds.height) {
      settle(true);
      return controller;
    }
    const center = {
      x: cardBounds.left + cardBounds.width / 2,
      y: cardBounds.top + cardBounds.height / 2,
    };

    animations.push(card.animate([
      { opacity: 1, transform: "scale(1)" },
      { opacity: 0, transform: "scale(.6)" },
    ], { duration: 560, delay: 160, easing: "ease-in", fill: "forwards" }));

    parts.forEach((element, index) => {
      const bounds = element.getBoundingClientRect();
      const dx = center.x - bounds.left - bounds.width / 2;
      const dy = center.y - bounds.top - bounds.height / 2;
      animations.push(element.animate([
        { opacity: index === 0 ? .35 : 1, transform: "translate(0px,0px) scale(1)", offset: 0 },
        { opacity: index === 0 ? .7 : 1, transform: `translate(${dx * .3}px,${dy * .3}px) scale(.8)`, offset: .42 },
        { opacity: index === 0 ? .7 : 1, transform: `translate(${dx * .8}px,${dy * .8}px) scale(.35)`, offset: .82 },
        { opacity: 0, transform: `translate(${dx}px,${dy}px) scale(.02)`, offset: 1 },
      ], {
        duration: CONVERGENCE_DURATION,
        delay: index * PART_STAGGER,
        easing: "cubic-bezier(.4,.05,.25,1)",
        fill: "forwards",
      }));
    });

    timeout = window.setTimeout(() => settle(true), TRANSITION_TIMEOUT);
    Promise.all(animations.map((animation) => animation.finished)).then(
      () => settle(true),
      () => settle(true),
    );
  } catch {
    cancelAnimations();
    settle(true);
  }

  return controller;
}
